<template>
	<div>
		<!-- Comparaison des doublons, ouverte depuis les contrôles automatiques. -->
		<shutter-panel ref="shutterPanel" :class="{ 'mb-4': shutter_open }" />

		<div class="row invoice-supplier-fiche">
			<div class="col-12 col-xl-5">
				<div class="box">
					<div v-for="row in rows" :key="row.label" class="row invoice-supplier-fiche__row">
						<div class="col-5 invoice-supplier-fiche__label">{{ $t(row.label) }}</div>
						<div class="col-7" :class="{ 'font-weight-bold': row.strong }">
							<router-link v-if="row.to" :to="row.to">{{ row.value }}</router-link>
							<template v-else>{{ row.value }}</template>
						</div>
					</div>
				</div>

				<div class="box">
					<h4>{{ $t('invoice.supplier_fiche_controls') }}</h4>
					<!-- Un doublon signalé appelle un arbitrage : la mention ouvre la comparaison des deux
					     documents plutôt que de laisser l'utilisateur chercher où le faire. -->
					<a v-if="invoice.suppliersocr_has_duplicate" href="#" class="text-danger" @click.prevent="openDuplicate">
						<font-awesome-icon :icon="['fal', 'times-circle']" />
						{{ $t('invoice.supplier_fiche_duplicate_ko') }}
					</a>
					<p v-else class="mb-0 text-success">
						<font-awesome-icon :icon="['fal', 'check-circle']" />
						{{ $t('invoice.supplier_fiche_duplicate_ok') }}
					</p>

					<!-- Coordonnées bancaires du document, confrontées à celles du fournisseur. Une
					     facture sans IBAN ne dit rien à ce sujet : la ligne se tait plutôt que de
					     rassurer sur un contrôle qui n'a pas eu lieu. -->
					<p v-if="iban_check && iban_check.status !== 'no_iban'" class="mb-0 mt-2" :class="iban_ok ? 'text-success' : 'text-danger'">
						<font-awesome-icon :icon="['fal', iban_ok ? 'check-circle' : 'times-circle']" />
						{{ $t('invoice.iban_control.' + iban_check.status) }}
					</p>
				</div>
			</div>

			<div class="col-12 col-xl-7">
				<div class="box">
					<!-- Le document affiché n'est le plus souvent qu'une représentation reconstruite de
					     la facture électronique : son original reste accessible en propre. -->
					<button v-if="!invoice.suppliersocr_manual" class="btn btn-primary mb-3" :disabled="downloading_xml" @click="downloadXml">
						{{ $t('invoice.supplier_fiche_download_xml') }}
						<font-awesome-icon v-if="downloading_xml" :icon="['fas', 'spinner']" pulse/>
						<font-awesome-icon v-else :icon="['fal', 'download']"/>
					</button>

					<iframe
						v-if="is_pdf"
						:src="invoice.suppliersocr_pdf_url"
						class="invoice-supplier-fiche__preview"
					></iframe>
					<img v-else-if="invoice.suppliersocr_pdf_url" :src="invoice.suppliersocr_pdf_url" class="w-100" />
					<p v-else class="mb-0">{{ $t('invoice.supplier_fiche_no_preview') }}</p>
				</div>
			</div>
		</div>
	</div>
</template>


<script type="text/javascript">
	import Invoice from "@/mixins/Invoice.js"
	import Shutter from "@/mixins/Shutter.js"
	import ShutterInvoice from "@/mixins/shutters-manager/Invoice.js"

	export default {
		name: "InvoiceSupplierFicheOverview",
		mixins: [Invoice, Shutter, ShutterInvoice],
		props: {
			invoice: { type: Object, default: () => ({}) },
			invoicesupplier_id: { type: [String, Number], default: null }
		},

		data() {
			return {
				rows: [],
				shutter_open: false,
				iban_check: null,
				downloading_xml: false
			}
		},

		computed: {
			/**
			 * Le document reçu est tantôt un PDF, tantôt une image selon la source : le premier
			 * s'affiche dans un cadre, la seconde directement.
			 */
			is_pdf() {
				return !!this.invoice.suppliersocr_pdf_url && this.invoice.suppliersocr_pdf_url.includes('.pdf')
			},

			/**
			 * Compte de règlement du document reconnu parmi ceux qu'un utilisateur a approuvés pour ce
			 * fournisseur. Tout le reste — compte inconnu, compte laissé en quarantaine — se signale.
			 */
			iban_ok() {
				return !!this.iban_check && this.iban_check.status === 'known_approved'
			},

			supplier() {
				if(!this.invoice.tier) {
					return ''
				}

				// Le SIREN complète la raison sociale quand il est connu : c'est lui qui identifie le
				// fournisseur sur le réseau des plateformes agréées.
				return this.invoice.tier.tiers_siren
					? this.invoice.tier.tiers_rs + ' — ' + this.invoice.tier.tiers_siren
					: this.invoice.tier.tiers_rs
			}
		},

		watch: {
			invoice: {
				handler: 'refreshOverview',
				immediate: true
			}
		},

		methods: {
			/**
			 * Recompose l'aperçu : les lignes de la facture et le contrôle de ses coordonnées bancaires.
			 */
			async refreshOverview() {
				await this.buildRows()

				this.iban_check = this.invoice.suppliersocr_id ? await this.checkInvoiceSupplierIban(this.invoice.suppliersocr_id) : null
			},

			/**
			 * Remet l'original de la facture électronique. Le téléchargement passe par la plateforme
			 * pour les factures reçues avant son archivage : il peut donc prendre un instant.
			 */
			async downloadXml() {
				this.downloading_xml = true
				await this.downloadInvoiceSupplierXml(this.invoice.suppliersocr_id)
				this.downloading_xml = false
			},

			/**
			 * Ouvre la comparaison des deux documents suspectés d'être le même. L'écran est celui de la
			 * liste de contrôle : les décisions qu'il propose — garder les deux, supprimer le plus
			 * ancien — n'ont pas à exister en double.
			 */
			async openDuplicate() {
				const invoices = await this.getDuplicationInvoiceSupplier(this.invoice.suppliersocr_id)

				// Le doublon a pu être arbitré entre-temps depuis un autre écran : il ne reste alors que
				// la facture courante, et il n'y a plus rien à comparer.
				if(!invoices || invoices.length < 2) {
					this.infoToast('invoice.supplier_fiche_duplicate_gone')
					return
				}

				this.shutter_open = true
				this.setUpControlDuplicate(invoices)
			},

			/**
			 * Rappelée par l'écran de comparaison — le nom est celui qu'attend sa définition. Une
			 * facture a pu y être supprimée : la fiche se recharge, et se dérobe si c'était celle-ci.
			 */
			loadInvoicesSupplier() {
				this.shutter_open = false
				this.$emit('invoice-updated')
			},

			/**
			 * Destination de la fiche d'un tiers, ou rien si le tiers n'est pas rattaché : une facture
			 * reste consultable avant d'avoir été rapprochée de son fournisseur.
			 *
			 * @param {Object} tiers Tiers tel que l'API le renvoie.
			 * @return {Object|null}
			 */
			tiersRoute(tiers) {
				if(!tiers || !tiers.tiers_id) {
					return null
				}

				return { name: 'tiersTableauBord', params: { tiers_id: tiers.tiers_id } }
			},

			/**
			 * Construit les lignes de l'aperçu. Le formatage des montants dépend de la devise, chargée
			 * de façon asynchrone : les lignes sont donc calculées ici et non dans un computed, qui
			 * afficherait les promesses au lieu des montants.
			 *
			 * Une ligne sans valeur est écartée plutôt que rendue vide — une facture manuelle n'a ni
			 * titre ni échéance.
			 */
			async buildRows() {
				const currency = this.invoice.suppliersocr_currency || 'EUR'

				const rows = [
					{ label: 'invoice.suppliersocr_title', value: this.invoice.suppliersocr_title },
					{ label: 'invoice.suppliersocr_num', value: this.invoice.suppliersocr_num },
					{ label: 'invoice.suppliersocr_date', value: this.formatDate(this.invoice.suppliersocr_date) },
					{ label: 'invoice.supplier_fiche_supplier', value: this.supplier, to: this.tiersRoute(this.invoice.tier) },
					{ label: 'invoice.supplier_fiche_entity', value: this.invoice.entity ? this.invoice.entity.tiers_rs : '', to: this.tiersRoute(this.invoice.entity) },
					{ label: 'invoice.suppliersocr_ht', value: await this.priceFormat(this.invoice.suppliersocr_ht, currency, true) },
					{ label: 'invoice.supplier_fiche_vat', value: await this.priceFormat(this.invoice.suppliersocr_vat, currency, true) },
					{ label: 'invoice.suppliersocr_ttc', value: await this.priceFormat(this.invoice.suppliersocr_ttc, currency, true), strong: true },
					{ label: 'invoice.supplier_fiche_source', value: this.invoice.source },
					{ label: 'invoice.supplier_fiche_duedate', value: this.formatDate(this.invoice.suppliersocr_duedate) }
				]

				this.rows = rows.filter(row => row.value !== null && row.value !== undefined && row.value !== '')
			}
		}
	}
</script>
